import { FlexColumn, FlexRow, LabeledInput, StyledButton, useLoading, useModal } from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { HeadingSmall, ParagraphMedium } from 'baseui/typography';
import { useEffect } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { useCamera } from '../../hooks/useCamera';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { deleteConvoy, postConvoy, updateConvoy } from '../../services/convoyService';
import { apiParamsState } from '../../storage/app';
import {
  editingConvoyState,
  selectedConvoyIdState,
  selectedConvoyState,
  updateConvoysState,
} from '../../storage/convoys';
import { assetIdsInViewState, assetsInViewState } from '../../storage/map';
import { Convoy } from '../../types/convoy';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import { AssetListPicker } from '../shared/assetListPicker/AssetListPicker';
import ConfirmModal from '../shared/ConfirmModal';
import LocalizedDatePicker from '../shared/LocalizedDatePicker';

export function ConvoyDetail() {
  const { theme, css } = useStyles();
  const [selectedConvoyId, setSelectedConvoyId] = useRecoilState(selectedConvoyIdState);
  const resetSelectedConvoyId = useResetRecoilState(selectedConvoyIdState);
  const convoy = useRecoilValue(selectedConvoyState);
  const [editingConvoy, setEditingConvoy] = useRecoilState(editingConvoyState);
  const resetEditingConvoy = useResetRecoilState(editingConvoyState);
  const apiParams = useRecoilValue(apiParamsState);
  const { openModal, closeModal, showModal } = useModal();
  const setUpdateConvoys = useSetRecoilState(updateConvoysState);
  const { loading, startLoading, stopLoading } = useLoading();
  const isNewConvoy = editingConvoy?._id === '';
  const { showFailFeedback, showDeletionFeedback, showPositiveFeedback } = useFeedback();
  const { fitToLngLats } = useCamera();
  const [assetIdsInView, setAssetIdsInView] = useRecoilState(assetIdsInViewState);
  const resetAssetIdsInView = useResetRecoilState(assetIdsInViewState);
  const assetsInView = useRecoilValue(assetsInViewState);

  useEffect(() => {
    resetEditingConvoy();
    return () => {
      resetEditingConvoy();
      resetAssetIdsInView();
    };
  }, [selectedConvoyId]);

  useEffect(() => {
    setAssetIdsInView(editingConvoy?.assignedAssets);
  }, [editingConvoy?.assignedAssets]);

  useEffect(() => {
    if (assetsInView?.length) {
      fitToLngLats(assetsInView.map((asset) => [asset.position_longitude, asset.position_latitude]));
    }
  }, [assetIdsInView, fitToLngLats, fitToLngLats]);

  if (editingConvoy === undefined) return null;

  const canSubmit =
    !!editingConvoy.name &&
    !!editingConvoy.startDate &&
    !!editingConvoy.endDate &&
    !!editingConvoy.assignedAssets.length &&
    editingConvoy.separationDistance !== undefined;
  const hasUpdates = !areEqual(convoy || {}, editingConvoy);

  const onDeleteConvoy = async () => {
    if (!apiParams || selectedConvoyId === undefined) return;
    const { error } = await deleteConvoy(apiParams.clientId, apiParams.subClientId, selectedConvoyId);
    if (!error) {
      showDeletionFeedback(FEEDBACK.deleted(FEEDBACK_PREFIXES.convoy, convoy?.name));
      setUpdateConvoys('update');
      resetSelectedConvoyId();
      closeModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedDeletion(FEEDBACK_PREFIXES.convoy, convoy?.name));
    }
  };

  const onInputChange = (value: string | boolean | number | null, field: keyof Convoy) => {
    setEditingConvoy((prev) => ({ ...prev!, [field]: value }));
  };

  const onSaveConvoy = async () => {
    startLoading();
    const { id, error } = isNewConvoy ? await postConvoy(editingConvoy) : await updateConvoy(editingConvoy);
    if (!error && id) {
      if (isNewConvoy) setSelectedConvoyId(id);
      showPositiveFeedback(
        isNewConvoy
          ? FEEDBACK.created(FEEDBACK_PREFIXES.convoy)
          : FEEDBACK.edited(FEEDBACK_PREFIXES.convoy, editingConvoy.name)
      );
      setUpdateConvoys(id);
    } else {
      showFailFeedback(
        error || isNewConvoy
          ? FEEDBACK.failedCreation(FEEDBACK_PREFIXES.convoy)
          : FEEDBACK.failedEdition(FEEDBACK_PREFIXES.convoy, convoy?.name)
      );
    }
    stopLoading();
  };

  const onAssetsSelectionChange = (assetIds: string[]) => {
    setEditingConvoy((prev) => ({ ...prev!, assignedAssets: assetIds }));
  };

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale600}
    >
      <HeadingSmall margin={0}>{isNewConvoy ? 'Nuevo convoy' : convoy?.name}</HeadingSmall>
      <FlexColumn classNames={css({ flex: 1 })}>
        <LabeledInput
          label="Nombre"
          value={editingConvoy.name}
          onChange={(value) => onInputChange(value, 'name')}
          required
        />
        <LabeledInput
          label="Distancia de separación (m)"
          value={editingConvoy.separationDistance}
          onChange={(value) => onInputChange(value, 'separationDistance')}
          type="number"
          required
        />
        <Checkbox
          checked={editingConvoy.active}
          onChange={(e) => onInputChange(e.target.checked, 'active')}
        >
          Activo
        </Checkbox>
        <LocalizedDatePicker
          value={[
            editingConvoy.startDate ? new Date(editingConvoy.startDate) : undefined,
            editingConvoy.endDate ? new Date(editingConvoy.endDate) : undefined,
          ]}
          startDateLabel="Fecha de inicio *"
          endDateLabel="Fecha de termino *"
          required
          size="compact"
          onClose={() => {}}
          onChange={({ date }) => {
            if (date instanceof Array) {
              if (date.length > 1 && date[0] && date[1]) {
                onInputChange(date[0].getTime(), 'startDate');
                onInputChange(date[1].getTime(), 'endDate');
              } else if (date[0]) {
                onInputChange(date[0].getTime(), 'startDate');
                onInputChange(null, 'endDate');
              }
            }
          }}
          range
          separateRangeInputs
        />
        <AssetListPicker
          assignedAssetIds={editingConvoy.assignedAssets}
          onAssignedChange={onAssetsSelectionChange}
          required
        />
      </FlexColumn>
      <FlexRow
        classNames={`${css({ alignItems: 'center', justifyContent: isNewConvoy ? 'flex-end' : 'space-between' })}`}
      >
        {!isNewConvoy && (
          <StyledButton
            kind="tertiary"
            onClick={openModal}
          >
            Eliminar
          </StyledButton>
        )}
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            kind="tertiary"
            onClick={resetSelectedConvoyId}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={onSaveConvoy}
            isLoading={loading}
            disabled={!canSubmit || !hasUpdates}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onDeleteConvoy}
        confirmText="Eliminar"
        description={<ParagraphMedium>¿Desea eliminar "{convoy?.name}"?</ParagraphMedium>}
      />
    </FloatingCardContent>
  );
}
